import { OperationVariables, TypedDocumentNode } from '@apollo/client'
import { DocumentNode } from 'graphql'
import { useEffect } from 'react'
import { usePaginatedQuery } from '../paginatedQuery/usePaginatedQuery'
import { PaginatedPropertyKey, PaginatedQueryVariables } from '../typings'
import { EagerPaginatedQueryOptions, EagerPaginatedQueryResult } from './index'

export function useEagerPaginatedQuery<
  TData extends object,
  TVariables extends PaginatedQueryVariables = OperationVariables,
  TProperty extends PaginatedPropertyKey<TData> = PaginatedPropertyKey<TData>
>(
  query: TypedDocumentNode<TData, TVariables>,
  options: EagerPaginatedQueryOptions<TData, TVariables, TProperty>
): EagerPaginatedQueryResult<TData, TVariables>

export function useEagerPaginatedQuery<
  TData extends object,
  TVariables extends PaginatedQueryVariables = OperationVariables,
  TProperty extends PaginatedPropertyKey<TData> = PaginatedPropertyKey<TData>
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: EagerPaginatedQueryOptions<TData, TVariables, TProperty>
): EagerPaginatedQueryResult<TData, TVariables> {
  const paginatedQueryResult = usePaginatedQuery(query, options)

  const { hasNextPage, loading, isLoadingMore, loadMore } = paginatedQueryResult

  useEffect(() => {
    if (hasNextPage && !loading && !isLoadingMore) {
      loadMore().catch(() => null)
    }
  }, [hasNextPage, isLoadingMore, loadMore, loading])

  return paginatedQueryResult
}
